/* Language <-> site sync
 *
 * Vitaviva has four "sites": Denmark (da, DKK), Sweden (sv, SEK), the Netherlands (nl, EUR) and
 * Germany (de, EUR). Every other country is a market on vitaviva.com that keeps whatever language
 * the visitor picks.
 *
 * Language pickers (desktop header, mobile drawer, footer) also send a country_code:
 *   da -> DK and sv -> SE, from anywhere
 *   nl -> NL and de -> DE, when the visitor is on one of the four sites
 *   en -> NL, when leaving the Danish or Swedish site (EUR = the Dutch site, per merchant brief)
 *
 * Currency/country pickers also send that site's language, so DKK lands on the Danish site in
 * Danish instead of the Danish market in English:
 *   DK -> da, SE -> sv, NL -> nl, DE -> de
 * Re-tapping the country you are already in only switches language for DK and SE ("DKK always
 * means the Danish site"); re-tapping EUR changes nothing.
 *
 * Implementation: we listen in the CAPTURE phase for clicks on the picker links and inject a
 * hidden input into the same form before LocalizationForm.onItemClick submits it. Shopify then
 * processes locale + country in a single navigation.
 */
(function () {
  'use strict'

  const LANG_TO_COUNTRY = {
    da: 'DK',
    sv: 'SE',
    en: 'NL',
    de: 'DE',
    nl: 'NL',
  }

  const COUNTRY_TO_LANG = {
    DK: 'da',
    SE: 'sv',
    NL: 'nl',
    DE: 'de',
  }

  // A pick that crosses domains (vitaviva.dk/.se -> vitaviva.com) goes through Shopify's redirect
  // handoff, and a visitor who already has a vitaviva.com session keeps that session's old country
  // (e.g. EUR picked on .dk, landed on /nl priced in GBP). The picked country rides along in the
  // URL fragment, which browsers carry across redirects, and we enforce it here with ?country=.
  const hop = location.hash.match(/^#vv-country=([A-Z]{2})$/)
  if (hop) {
    const url = new URL(location.href)
    url.hash = ''
    if (window.Shopify && window.Shopify.country && window.Shopify.country !== hop[1]) {
      url.searchParams.set('country', hop[1])
      location.replace(url.toString())
      return
    }
    history.replaceState(history.state, '', url.toString())
  }

  function current() {
    const s = window.Shopify || {}
    return {
      country: s.country || '',
      locale: (s.locale || '').toLowerCase().split('-')[0],
    }
  }

  // A Norwegian, Swiss, UK or US visitor on vitaviva.com who picks English or German keeps their
  // own country and currency.
  function targetCountry(locale) {
    const now = current()
    // Re-selecting the language you already have is not a request to change site.
    if (locale === now.locale) return null
    const alwaysMoves = locale === 'da' || locale === 'sv'
    if (!alwaysMoves && now.country && !COUNTRY_TO_LANG[now.country]) return null
    // English on the Dutch or German site stays there.
    if (locale === 'en' && (now.country === 'NL' || now.country === 'DE')) return null
    const target = LANG_TO_COUNTRY[locale]
    return target === now.country ? null : target
  }

  function targetLocale(country) {
    const retap = country === current().country
    return retap && country !== 'DK' && country !== 'SE' ? null : COUNTRY_TO_LANG[country]
  }

  function ensureInput(form, name, value) {
    let input = form.querySelector('input[name="' + name + '"]')
    // No value: drop any input an earlier pick left behind (e.g. restored after Back).
    if (!value) {
      if (input) input.remove()
      return
    }
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      form.appendChild(input)
    }
    input.value = value
  }

  function markCountry(form, country) {
    const action = (form.getAttribute('action') || '/localization').split('#')[0]
    form.setAttribute('action', country ? action + '#vv-country=' + country : action)
  }

  function localeOf(value) {
    return (value || '').toLowerCase().split('-')[0]
  }

  function bindLanguageForm(form) {
    // Each language <a> carries the locale in its hreflang/lang attribute.
    form.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest('a[hreflang], a[lang]')
        if (!link || !form.contains(link)) return
        const country = targetCountry(localeOf(link.getAttribute('hreflang') || link.getAttribute('lang')))
        ensureInput(form, 'country_code', country)
        markCountry(form, country)
      },
      true /* capture: run before LocalizationForm.onItemClick */
    )
  }

  function bindCountryForm(form) {
    // Currency and country list links carry the target country in data-value.
    form.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest('a[data-value]')
        if (!link || !form.contains(link)) return
        ensureInput(form, 'locale_code', targetLocale(link.dataset.value))
        markCountry(form, link.dataset.value)
      },
      true /* capture: run before LocalizationForm.onItemClick */
    )
  }

  function bind(selector, binder) {
    document.querySelectorAll(selector).forEach(function (form) {
      if (form.dataset.langCurrencySyncBound === '1') return
      form.dataset.langCurrencySyncBound = '1'
      binder(form)
    })
  }

  function attach() {
    // Desktop header, mobile drawer (…Mobile) and footer. The noscript forms only matter without
    // JS, where this file does not run anyway.
    bind('form[id^="HeaderLanguageForm"], form[id^="FooterLanguageForm"]', bindLanguageForm)
    bind('form[id^="HeaderCountryForm"], form[id^="FooterCountryForm"]', bindCountryForm)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach)
  } else {
    attach()
  }

  // Re-attach if the header gets re-rendered (e.g. by section refresh)
  document.addEventListener('shopify:section:load', attach)
})()
